import React from 'react';
import { Copy, RefreshCw, PlusCircle, User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, User as UserType } from '@/lib/api';
import { cn } from '@/lib/utils';

interface DuplicateSettingsProps {
  duplicateMode: 'SKIP' | 'UPDATE' | 'CREATE_NEW';
  onDuplicateModeChange: (mode: 'SKIP' | 'UPDATE' | 'CREATE_NEW') => void;
  defaultAssignee: string;
  onAssigneeChange: (userId: string) => void;
  defaultPriority?: string;
  onPriorityChange?: (priority: string) => void;
  defaultStage?: string;
  onStageChange?: (stage: string) => void;
}

const duplicateModes = [
  {
    value: 'SKIP' as const,
    label: 'Skip duplicates',
    description: 'If a lead with the same email already exists, skip it',
    icon: Copy,
    color: 'text-yellow-600 bg-yellow-100',
  },
  {
    value: 'UPDATE' as const,
    label: 'Update existing',
    description: 'Update existing leads with new data from the import',
    icon: RefreshCw,
    color: 'text-blue-600 bg-blue-100',
  },
  {
    value: 'CREATE_NEW' as const,
    label: 'Create new',
    description: 'Always create new leads, even if duplicates exist',
    icon: PlusCircle,
    color: 'text-green-600 bg-green-100',
  },
];

const priorities = [
  { value: '', label: 'Use from file (if available)' },
  { value: 'HOT', label: 'Hot' },
  { value: 'WARM', label: 'Warm' },
  { value: 'COLD', label: 'Cold' },
];

const stages = [
  { value: '', label: 'Use from file (if available)' },
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'DISCUSSION', label: 'Discussion' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
];

export const DuplicateSettings: React.FC<DuplicateSettingsProps> = ({
  duplicateMode,
  onDuplicateModeChange,
  defaultAssignee,
  onAssigneeChange,
  defaultPriority = '',
  onPriorityChange,
  defaultStage = '',
  onStageChange,
}) => {
  // Fetch users for assignee dropdown
  const { data: users = [] } = useQuery<UserType[]>({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
  });

  return (
    <div className="space-y-6">
      {/* Duplicate Handling */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Duplicate Handling</h4>
        <p className="text-sm text-gray-500 mb-4">
          Choose how to handle leads that already exist in your database (matched by email)
        </p>

        <div className="space-y-3">
          {duplicateModes.map((mode) => {
            const Icon = mode.icon;
            const isSelected = duplicateMode === mode.value;

            return (
              <label
                key={mode.value}
                className={cn(
                  'flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors',
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                )}
              >
                <input
                  type="radio"
                  name="duplicateMode"
                  value={mode.value}
                  checked={isSelected}
                  onChange={() => onDuplicateModeChange(mode.value)}
                  className="mt-1"
                />
                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', mode.color)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className={cn('text-sm font-medium', isSelected ? 'text-blue-900' : 'text-gray-900')}>
                    {mode.label}
                  </p>
                  <p className={cn('text-xs mt-0.5', isSelected ? 'text-blue-700' : 'text-gray-500')}>
                    {mode.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Default Values */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Default Values</h4>
        <p className="text-sm text-gray-500 mb-4">
          Set default values for leads that don't have these fields in the import file
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Default Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Default Assignee
            </label>
            <select
              value={defaultAssignee}
              onChange={(e) => onAssigneeChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- None (leave unassigned) --</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </select>
          </div>

          {/* Default Priority */}
          {onPriorityChange && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Priority
              </label>
              <select
                value={defaultPriority}
                onChange={(e) => onPriorityChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {priorities.map((priority) => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Default Stage */}
          {onStageChange && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Stage
              </label>
              <select
                value={defaultStage}
                onChange={(e) => onStageChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {stages.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Import Notes</h5>
        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
          <li>Duplicates are detected by matching email addresses</li>
          <li>Leads without email addresses will always be created as new</li>
          <li>Default values only apply when the field is empty in the import file</li>
          <li>You can always edit leads after import is complete</li>
        </ul>
      </div>
    </div>
  );
};
